import { createContext } from 'react';
import {
	TFileId,
	IItemForGetShortInfo,
	IFileData,
	TTypeOfServiceProvided,
	IUpdateCustomer,
	ICustomerLanguage,
	ITags,
	CardBrands,
	TUseTipsByDefault,
} from 'types';

export type TEditCustomerVisibleForm = '' | 'mainForm' | 'localizationForm' | 'styleForm' | 'bpaForm';
export type TEditCustomerSelectedClientType = null | TTypeOfServiceProvided;
export type TTypeOfEditCustomerModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'success'
	| 'error'
	| 'imageFileSizeExceeded'
	| 'htmlFileSizeExceeded'
	| 'fileUploadError'
	| 'invalidFileFormat'
	| 'customerNameAlreadyExist';
export interface IFormMccCard {
	id: string;
	brand: CardBrands | null;
	mcc: Nullable<number>;
}
export interface IInitialFormData {
	name: string;
	email: string;
	emailBodyTemplate: string;
	clientType: TEditCustomerSelectedClientType;
	billTemplateFileData: IFileData;
	billAdvertisingImageData: IFileData;
	billAdvertisingImage: Nullable<string>;
	billAdvertisingLink: Nullable<string>;
	priceType: Nullable<string>;
	tips0: null | number;
	tips1: null | number;
	tips2: null | number;
	tips3: null | number;
	defaultTips: TUseTipsByDefault;
	isShowLoyaltyProgramButtons: boolean;
	baseMcc: Nullable<number>;
	mainMccCardBrand: CardBrands | null;
	mainMccCardMcc: Nullable<number>;
	mccCards: IFormMccCard[];
	vivaWalletCredentialsId: Nullable<string>;
	ads: string;
	menuVersion: Nullable<string>;
	contentVersion: Nullable<string>;
	mixpanelToken: string;
	stopListSyncIntervalMinutes: Nullable<number>;
	IDSelectedCustomerDefaultLanguage: Nullable<string>;
	IDsSelectedCustomerLanguages: string[];
	filePath: Nullable<string>;
	tags: ITags[];
	isActiveBpa: boolean;
}

export interface IEditCustomerState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	visibleForm: TEditCustomerVisibleForm;
	customerId: string;
	filePath: Nullable<string>;
	fileId: TFileId;
	sourceUrlLocally: Nullable<string>;
	name: string;
	email: string;
	emailBodyTemplate: string;
	clientType: TEditCustomerSelectedClientType;
	billTemplateFileData: IFileData;
	billTemplateFilePath: Nullable<string>;
	billAdvertisingImageData: IFileData;
	billAdvertisingImage: Nullable<string>;
	billAdvertisingLink: Nullable<string>;
	priceType: Nullable<string>;
	tips0: null | number;
	tips1: null | number;
	tips2: null | number;
	tips3: null | number;
	defaultTips: TUseTipsByDefault;
	isShowLoyaltyProgramButtons: boolean;
	baseMcc: Nullable<number>;
	mainMccCardBrand: CardBrands | null;
	mainMccCardMcc: Nullable<number>;
	mccCards: IFormMccCard[];
	vivaWalletCredentialsId: Nullable<string>;
	ads: string;
	menuVersion: string;
	contentVersion: string;
	mixpanelToken: string;
	stopListSyncIntervalMinutes: Nullable<number>;
	fetchedLanguages: IItemForGetShortInfo[];
	IDSelectedCustomerDefaultLanguage: Nullable<string>;
	IDsSelectedCustomerLanguages: string[];
	customerLanguages: ICustomerLanguage[];
	tags: ITags[];
	isActiveBpa: boolean;
	dataForSubmit: IUpdateCustomer | null;
	typeOfModalWindow: TTypeOfEditCustomerModalWindow;
}

export const editCustomerInitialState: IEditCustomerState = {
	needRefetchData: false,
	initialFormData: null,
	visibleForm: '',
	customerId: '',
	filePath: null,
	fileId: null,
	sourceUrlLocally: null,
	name: '',
	email: '',
	emailBodyTemplate: '',
	clientType: null,
	billTemplateFileData: {
		fileId: '',
		fileHash: null,
		fileName: null,
	},
	billTemplateFilePath: null,
	billAdvertisingImageData: {
		fileId: '',
		fileHash: null,
		fileName: null,
	},
	billAdvertisingImage: null,
	billAdvertisingLink: null,
	priceType: null,
	tips0: null,
	tips1: null,
	tips2: null,
	tips3: null,
	defaultTips: null,
	isShowLoyaltyProgramButtons: false,
	baseMcc: null,
	mainMccCardBrand: null,
	mainMccCardMcc: null,
	mccCards: [],
	vivaWalletCredentialsId: null,
	ads: '',
	menuVersion: '',
	contentVersion: '',
	mixpanelToken: '',
	stopListSyncIntervalMinutes: 5,
	fetchedLanguages: [],
	IDSelectedCustomerDefaultLanguage: null,
	IDsSelectedCustomerLanguages: [],
	customerLanguages: [],
	tags: [],
	isActiveBpa: false,
	dataForSubmit: null,
	typeOfModalWindow: null,
};

export interface IEditCustomerContext extends IEditCustomerState {
	refetchData(value: boolean): void;
	changeVisibleForm(value: TEditCustomerVisibleForm): void;
	setFile(fileId: TFileId, sourceUrlLocally: Nullable<string>): void;
	deletePhoto(): void;
	changeName(value: string): void;
	changeEmail(value: string): void;
	changeEmailBodyTemplate(value: string): void;
	setClientType(value: TEditCustomerSelectedClientType): void;
	changeBillTemplateFileData(value: IFileData): void;
	changeBillAdvertisingImageData(value: IFileData): void;
	changeBillAdvertisingLink(value: string): void;
	deleteBillAdvertisingImage(): void;
	changePriceType(value: string): void;
	setTips0(value: null | number): void;
	setTips1(value: null | number): void;
	setTips2(value: null | number): void;
	setTips3(value: null | number): void;
	setDefaultTips(value: TUseTipsByDefault): void;
	toggleShowLoyaltyProgramButtons(): void;
	changeBaseMcc(value: Nullable<number>): void;
	addMccCard(value: IFormMccCard): void;
	deleteMccCard(id: string): void;
	changeMainMccCardBrand(value: CardBrands): void;
	changeMccCardBrand(id: string, value: CardBrands): void;
	changeMainMccCardMcc(value: Nullable<number>): void;
	changeMccCardMcc(id: string, value: Nullable<number>): void;
	setVivaWalletCredentialsId(value: Nullable<string>): void;
	setAds(value: string): void;
	changeMenuVersion(value: string): void;
	changeContentVersion(value: string): void;
	setMixpanelToken(value: string): void;
	setStopListSyncIntervalMinutes(value: Nullable<number>): void;
	setFetchedLanguages(value: IItemForGetShortInfo[]): void;
	setCustomerDefaultLanguage(value: Nullable<string>): void;
	setCustomerLanguages(value: string[]): void;
	addTags(): void;
	changeTags(value: string, item: number, languageId: string): void;
	deleteTag(item: number): void;
	toggleActivateBPA(): void;
	setDataForSubmit(data: IUpdateCustomer | null): void;
	changeTypeOfModalWindow(value: TTypeOfEditCustomerModalWindow): void;
	resetFields(): void;
}

export const EditCustomerContext = createContext<IEditCustomerContext>({
	refetchData: () => undefined,
	changeVisibleForm: () => undefined,
	setFile: () => undefined,
	deletePhoto: () => undefined,
	changeName: () => undefined,
	changeEmail: () => undefined,
	changeEmailBodyTemplate: () => undefined,
	setClientType: () => undefined,
	changeBillTemplateFileData: () => undefined,
	changeBillAdvertisingImageData: () => undefined,
	changeBillAdvertisingLink: () => undefined,
	deleteBillAdvertisingImage: () => undefined,
	changePriceType: () => undefined,
	setTips0: () => undefined,
	setTips1: () => undefined,
	setTips2: () => undefined,
	setTips3: () => undefined,
	setDefaultTips: () => undefined,
	toggleShowLoyaltyProgramButtons: () => undefined,
	changeBaseMcc: () => undefined,
	addMccCard: () => undefined,
	deleteMccCard: () => undefined,
	changeMainMccCardBrand: () => undefined,
	changeMccCardBrand: () => undefined,
	changeMainMccCardMcc: () => undefined,
	changeMccCardMcc: () => undefined,
	setVivaWalletCredentialsId: () => undefined,
	setAds: () => undefined,
	changeMenuVersion: () => undefined,
	changeContentVersion: () => undefined,
	setMixpanelToken: () => undefined,
	setStopListSyncIntervalMinutes: () => undefined,
	setFetchedLanguages: () => undefined,
	setCustomerDefaultLanguage: () => undefined,
	setCustomerLanguages: () => undefined,
	addTags: () => undefined,
	changeTags: () => undefined,
	deleteTag: () => undefined,
	toggleActivateBPA: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	resetFields: () => undefined,
	...editCustomerInitialState,
});
